import React from 'react'
import { Form } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarAlt, faPlus, faTrashAlt } from '@fortawesome/pro-duotone-svg-icons'
import GlobalStyles from './styles'
import { normalizeScheduleTimeValue, scheduleTimeValueForInput } from './schedule-time'
import './schedule-recurrence-editor.css'

export type ScheduleRecurrenceType = 'daily' | 'weekly' | 'monthly'

export type ScheduleBlackoutDate = {
  date: string
  reason?: string
}

export type ScheduleRecurrenceEditorValue = {
  schedule_type: ScheduleRecurrenceType
  recurrence_interval: number
  execution_times: string[]
  days_of_week: number[]
  date_rule: 'day_of_month' | 'month_segment' | null
  day_of_month: number | null
  week_segment: 'first' | 'second' | 'third' | 'fourth' | null
  day_of_week: number | null
  start_date: string | null
  end_date: string | null
  blackout_dates: ScheduleBlackoutDate[]
}

type WeekdayOption = {
  value: number
  shortLabel: string
}

type ScheduleRecurrenceEditorProps = {
  value: ScheduleRecurrenceEditorValue
  onChange: (nextValue: ScheduleRecurrenceEditorValue) => void
  weekdayOptions: WeekdayOption[]
  timezone: string
  todayDateValue: string
  enabledFrequencies?: ScheduleRecurrenceType[]
  allowMultipleTimes?: boolean
  showBlackoutDates?: boolean
  timeStepSeconds?: number
  fieldErrors?: {
    start_date?: string
    end_date?: string
    blackout_dates?: string
  }
}

const defaultFrequencies: ScheduleRecurrenceType[] = ['daily', 'weekly', 'monthly']

export const ScheduleRecurrenceEditor = ({
  value,
  onChange,
  weekdayOptions,
  timezone,
  todayDateValue,
  enabledFrequencies = defaultFrequencies,
  allowMultipleTimes = true,
  showBlackoutDates = true,
  timeStepSeconds = 60,
  fieldErrors,
}: ScheduleRecurrenceEditorProps) => {
  const update = (partial: Partial<ScheduleRecurrenceEditorValue>) => {
    onChange({ ...value, ...partial })
  }

  const intervalUnitLabel =
    value.schedule_type === 'daily'
      ? value.recurrence_interval === 1
        ? 'day'
        : 'days'
      : value.schedule_type === 'weekly'
        ? value.recurrence_interval === 1
          ? 'week'
          : 'weeks'
        : value.recurrence_interval === 1
          ? 'month'
          : 'months'

  const formatTimeLabel = (rawTime: string) => {
    const [hourValue, minuteValue] = normalizeScheduleTimeValue(rawTime).split(':')
    const hours = Number(hourValue)
    const minutes = Number(minuteValue)
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return rawTime
    return new Date(2026, 0, 1, hours, minutes, 0).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const formatDateLabel = (dateValue: string | null) => {
    if (!dateValue) return null
    const [year, month, day] = dateValue.split('-').map(Number)
    if (!year || !month || !day) return dateValue
    return new Date(year, month - 1, day).toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const recurrenceSentence = (() => {
    const times = (value.execution_times || []).map(formatTimeLabel)
    const timesLabel =
      times.length === 0
        ? 'at no execution times yet'
        : times.length === 1
          ? `at ${times[0]}`
          : `at ${times.slice(0, -1).join(', ')}, and ${times[times.length - 1]}`

    const recurrenceInterval = value.recurrence_interval || 1
    const cadenceLabel =
      recurrenceInterval === 1
        ? value.schedule_type === 'daily'
          ? 'Every day'
          : value.schedule_type === 'weekly'
            ? 'Every week'
            : 'Every month'
        : `Every ${recurrenceInterval} ${intervalUnitLabel}`

    const startLabel = formatDateLabel(value.start_date)
    const endLabel = formatDateLabel(value.end_date)
    const dateWindow = startLabel
      ? endLabel
        ? `Active from ${startLabel} through ${endLabel}.`
        : `Active starting ${startLabel} with no end date.`
      : endLabel
        ? `Active through ${endLabel}.`
        : 'No date window selected yet.'

    let cadenceSentence = ''

    if (value.schedule_type === 'daily') {
      cadenceSentence = `${cadenceLabel} ${timesLabel}.`
    }

    if (value.schedule_type === 'weekly') {
      const selectedDays = weekdayOptions
        .filter(({ value: dayValue }) => value.days_of_week.includes(dayValue))
        .map(({ shortLabel }) => shortLabel)
      const dayLabel = selectedDays.length
        ? `on ${selectedDays.join(', ')}`
        : 'with no weekdays selected yet'
      cadenceSentence = `${cadenceLabel} ${dayLabel}, ${timesLabel}.`
    }

    if (value.schedule_type === 'monthly' && (value.date_rule || 'day_of_month') === 'month_segment') {
      const selectedWeekday = weekdayOptions.find(({ value: dayValue }) => dayValue === (value.day_of_week || 1))
      const weekLabel = value.week_segment || 'first'
      cadenceSentence = `${cadenceLabel} on the ${weekLabel} ${selectedWeekday?.shortLabel || 'weekday'} of the month, ${timesLabel}.`
    }

    if (value.schedule_type === 'monthly' && !cadenceSentence) {
      cadenceSentence = `${cadenceLabel} on day ${value.day_of_month || 1} of the month, ${timesLabel}.`
    }

    return `${cadenceSentence} ${dateWindow}`
  })()

  const toggleWeekday = (dayValue: number) => {
    const isActive = value.days_of_week.includes(dayValue)
    update({
      days_of_week: isActive
        ? value.days_of_week.filter((day) => day !== dayValue)
        : value.days_of_week.concat(dayValue).sort((left, right) => left - right),
    })
  }

  const updateExecutionTime = (index: number, nextTimeValue: string) => {
    const nextTimes = [...value.execution_times]
    nextTimes[index] = normalizeScheduleTimeValue(nextTimeValue)
    update({ execution_times: nextTimes })
  }

  const removeExecutionTime = (index: number) => {
    update({ execution_times: value.execution_times.filter((_, executionTimeIndex) => executionTimeIndex !== index) })
  }

  const addExecutionTime = () => {
    update({ execution_times: value.execution_times.concat('12:00:00') })
  }

  const updateBlackout = (index: number, field: 'date' | 'reason', fieldValue: string) => {
    update({
      blackout_dates: value.blackout_dates.map((blackoutDate, blackoutIndex) =>
        blackoutIndex === index ? { ...blackoutDate, [field]: fieldValue } : blackoutDate
      ),
    })
  }

  const addBlackoutDate = () => {
    update({
      blackout_dates: value.blackout_dates.concat({ date: '', reason: '' }),
    })
  }

  const removeBlackoutDate = (index: number) => {
    update({
      blackout_dates: value.blackout_dates.filter((_, blackoutIndex) => blackoutIndex !== index),
    })
  }

  return (
    <>
      <div className="section">
        <div className="section-heading">
          <FontAwesomeIcon icon={faCalendarAlt} />
          Recurrence
        </div>
        <div className="recurrence-pills">
          {enabledFrequencies.map((frequency) => (
            <button
              className={`recurrence-pill${value.schedule_type === frequency ? ' active' : ''}`}
              key={frequency}
              type="button"
              onClick={() => update({ schedule_type: frequency })}
            >
              {frequency[0].toUpperCase() + frequency.slice(1)}
            </button>
          ))}
        </div>
        <Form.Group className="recurrence-interval-group">
          <div className="recurrence-interval-row">
            <span className="recurrence-interval-prefix">Every</span>
            <Form.Control
              type="number"
              min={1}
              max={365}
              value={value.recurrence_interval || 1}
              onChange={(event) =>
                update({
                  recurrence_interval: Math.min(365, Math.max(1, Math.floor(Number(event.target.value) || 1))),
                })
              }
            />
            <span className="recurrence-interval-suffix">{intervalUnitLabel}</span>
          </div>
          <div className="section-copy recurrence-interval-help">
            Set to 2 for every other {value.schedule_type === 'daily' ? 'day' : value.schedule_type === 'weekly' ? 'week' : 'month'}.
          </div>
        </Form.Group>
        {value.schedule_type === 'weekly' ? (
          <div style={{ marginTop: '0.8rem' }}>
            <div className="day-pills">
              {weekdayOptions.map(({ value: dayValue, shortLabel }) => {
                const active = value.days_of_week.includes(dayValue)
                return (
                  <button
                    className={`day-pill${active ? ' active' : ''}`}
                    key={dayValue}
                    type="button"
                    onClick={() => toggleWeekday(dayValue)}
                  >
                    {shortLabel}
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
        {value.schedule_type === 'monthly' ? (
          <div style={{ marginTop: '0.8rem' }}>
            <Form.Group>
              <Form.Label>Monthly Rule</Form.Label>
              <Form.Select
                value={value.date_rule || 'day_of_month'}
                onChange={(event) =>
                  update({
                    date_rule: event.target.value as 'day_of_month' | 'month_segment',
                    day_of_month: event.target.value === 'day_of_month' ? value.day_of_month || 1 : null,
                    week_segment: event.target.value === 'month_segment' ? value.week_segment || 'first' : null,
                    day_of_week: event.target.value === 'month_segment' ? value.day_of_week || 1 : null,
                  })
                }
              >
                <option value="day_of_month">Day of month</option>
                <option value="month_segment">Week of month</option>
              </Form.Select>
            </Form.Group>
            {(value.date_rule || 'day_of_month') === 'day_of_month' ? (
              <Form.Group style={{ marginTop: '0.75rem' }}>
                <Form.Label>Day of Month</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={31}
                  value={value.day_of_month || 1}
                  onChange={(event) => update({ day_of_month: Number(event.target.value) || 1 })}
                />
                {value.day_of_month && value.day_of_month > 28 ? (
                  <div className="section-copy">Shorter months use the last calendar day instead.</div>
                ) : null}
              </Form.Group>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '0.75rem',
                  marginTop: '0.75rem',
                }}
              >
                <Form.Group>
                  <Form.Label>Week</Form.Label>
                  <Form.Select
                    value={value.week_segment || 'first'}
                    onChange={(event) =>
                      update({ week_segment: event.target.value as 'first' | 'second' | 'third' | 'fourth' })
                    }
                  >
                    <option value="first">First</option>
                    <option value="second">Second</option>
                    <option value="third">Third</option>
                    <option value="fourth">Fourth</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Weekday</Form.Label>
                  <Form.Select
                    value={value.day_of_week || 1}
                    onChange={(event) => update({ day_of_week: Number(event.target.value) })}
                  >
                    {weekdayOptions.map(({ value: dayValue, shortLabel }) => (
                      <option value={dayValue} key={dayValue}>
                        {shortLabel}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            )}
          </div>
        ) : null}
        <div style={{ marginTop: '0.8rem' }}>
          <Form.Group>
            <Form.Label>Local Execution Times</Form.Label>
            <div className="execution-times-list">
              {value.execution_times.map((executionTime, index) => (
                <div className="execution-time-row" key={index}>
                  <Form.Control
                    type="time"
                    step={timeStepSeconds}
                    value={scheduleTimeValueForInput(executionTime)}
                    onChange={(event) => updateExecutionTime(index, event.target.value)}
                  />
                  {allowMultipleTimes && value.execution_times.length > 1 ? (
                    <button
                      className="icon-button"
                      type="button"
                      aria-label={`Remove execution time ${index + 1}`}
                      onClick={() => removeExecutionTime(index)}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            {allowMultipleTimes ? (
              <button className="add-time-button" type="button" onClick={addExecutionTime}>
                <FontAwesomeIcon icon={faPlus} /> Add another time
              </button>
            ) : null}
          </Form.Group>
          <div className="section-copy timezone-note">Warehouse timezone: {timezone}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.75rem' }}>
          <Form.Group>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              min={todayDateValue}
              value={value.start_date || ''}
              isInvalid={Boolean(fieldErrors?.start_date)}
              onChange={(event) => update({ start_date: event.target.value || null })}
            />
            <Form.Control.Feedback type="invalid">{fieldErrors?.start_date}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              min={value.start_date || todayDateValue}
              value={value.end_date || ''}
              isInvalid={Boolean(fieldErrors?.end_date)}
              onChange={(event) => update({ end_date: event.target.value || null })}
            />
            <Form.Control.Feedback type="invalid">{fieldErrors?.end_date}</Form.Control.Feedback>
          </Form.Group>
        </div>
        <div className="section-copy" style={{ marginTop: '0.65rem' }}>
          End date is optional. Leave it blank to keep this schedule open-ended.
        </div>
        <div className="recurrence-summary">
          <div className="recurrence-summary-header">
            <div className="recurrence-summary-title">Summary</div>
          </div>
          <div>{recurrenceSentence}</div>
        </div>
      </div>

      {showBlackoutDates ? (
        <div className="section">
          <div className="section-heading">Blackout Dates</div>
          <div className="section-copy">Add one-off skip dates without deactivating the schedule.</div>
          <div className="blackout-list">
            {value.blackout_dates.map((blackoutDate, index) => (
              <div className="blackout-row" key={index}>
                <Form.Control
                  type="date"
                  min={todayDateValue}
                  value={blackoutDate.date}
                  onChange={(event) => updateBlackout(index, 'date', event.target.value)}
                />
                <Form.Control
                  value={blackoutDate.reason || ''}
                  onChange={(event) => updateBlackout(index, 'reason', event.target.value)}
                  placeholder="Optional reason"
                />
                <button className="icon-button" type="button" onClick={() => removeBlackoutDate(index)}>
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </div>
            ))}
          </div>
          <GlobalStyles.Button type="button" onClick={addBlackoutDate} style={{ marginTop: '0.75rem' }}>
            <FontAwesomeIcon icon={faPlus} /> Add Blackout Date
          </GlobalStyles.Button>
          {fieldErrors?.blackout_dates ? <div className="validation-error">{fieldErrors.blackout_dates}</div> : null}
        </div>
      ) : null}
    </>
  )
}
